// Traverses and Terzaghi weighting (stereo TRZ-01..04). GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t, tn } from "../../i18n/index.js";
import { $, esc } from "../../lib/dom.js";
import { fmt } from "../../lib/format.js";
import { pushHistory } from "../../state/store.js";
import { anyBiased } from "../../state/derived.js";
import { addTraverse, removeTraverse, rowsOnTraverse, setTerzaghi, setTraverseKind, setTraverseValue } from "../../state/ops.js";
import { X, P, on, update, onRender } from "./ctx.js";

export function renderTraverse() {
  const el = $("trlist"), p = P(), d = p.data;
  el.innerHTML = d.traverses.map((tv, k) => {
    const tr = tv.tr;
    const [la, lb, a, b] = tr.kind === "linear" ? ["Trend", "Plunge", tr.trend, tr.plunge]
      : tr.kind === "planar" ? ["Dip", "Dip dir", tr.dip, tr.dipDir] : ["", "", 0, 0];
    return `<div class="trcard" data-t="${k}">
      <div class="setrow"><input class="setname" data-tname="${k}" value="${esc(tv.name)}" aria-label="${t("Traverse name")}">
        <span class="muted small">${tn(rowsOnTraverse(p, tv.id, k === 0), "{n} row", "{n} rows")}</span>
        ${d.traverses.length > 1 ? `<button class="icon" data-tdel="${k}" aria-label="${t("Delete {name}", { name: esc(tv.name) })}" title="${t("Delete traverse")}">×</button>` : ""}</div>
      <select data-tkind="${k}" aria-label="${t("Kind of traverse {name}", { name: esc(tv.name) })}">
        <option value="spot" ${tr.kind === "spot" ? "selected" : ""}>${t("Spot mapping (no bias correction)")}</option>
        <option value="linear" ${tr.kind === "linear" ? "selected" : ""}>${t("Linear scanline / borehole")}</option>
        <option value="planar" ${tr.kind === "planar" ? "selected" : ""}>${t("Planar (rock face)")}</option></select>
      ${tr.kind === "spot" ? "" : `<div class="row" style="margin-top:.35rem">
        <label class="field" style="flex:1;margin:0"><span>${la}</span><input data-ta="${k}" type="number" step="any" value="${+a.toFixed(2)}"></label>
        <label class="field" style="flex:1;margin:0"><span>${lb}</span><input data-tb="${k}" type="number" step="any" value="${+b.toFixed(2)}"></label></div>`}
    </div>`;
  }).join("");
  $<HTMLInputElement>("tr-min").value = String(d.terzaghi.minBiasDeg);
  const use = $<HTMLInputElement>("tr-use"); use.checked = d.terzaghi.use; use.disabled = !anyBiased(p);
  const wmax = X.d.tWeights.length ? Math.max(...X.d.tWeights) : 1;
  $("tr-info").textContent = !anyBiased(p) ? t("All traverses are spot mapping, so no bias correction applies") :
    t("Largest weight {w} (capped at {cap} by the minimum angle {a}°)", { w: fmt(wmax, 2), cap: fmt(1 / Math.sin(d.terzaghi.minBiasDeg * S.DEG), 2), a: d.terzaghi.minBiasDeg }) +
    (d.terzaghi.use ? " · " + t("applied to contours and set statistics") : "");
}

export function mountTraverse() {
  onRender(() => renderTraverse());
  const list = $("trlist");
  on(list, "change", (e: Event) => {
    const el = e.target as HTMLInputElement | HTMLSelectElement, ds = el.dataset, p = P();
    pushHistory();
    if (ds.tname != null) { const v = el.value.trim(); if (v) p.data.traverses[+ds.tname].name = v.slice(0, 40); }
    else if (ds.tkind != null) setTraverseKind(p, +ds.tkind, el.value as S.Traverse["kind"]);
    else if (ds.ta != null) setTraverseValue(p, +ds.ta, "a", Number(el.value));
    else if (ds.tb != null) setTraverseValue(p, +ds.tb, "b", Number(el.value));
    update(true);
  });
  on(list, "click", (e: Event) => {
    const b = (e.target as HTMLElement).closest("[data-tdel]") as HTMLElement | null; if (!b) return;
    pushHistory(); removeTraverse(P(), +b.dataset.tdel!); update(true);
  });
  on($("tr-add"), "click", () => { pushHistory(); addTraverse(P()); update(true); });
  ["tr-min", "tr-use"].forEach((id) => on($(id), "change", () => {
    pushHistory(); setTerzaghi(P(), Number($<HTMLInputElement>("tr-min").value), $<HTMLInputElement>("tr-use").checked); update(false);
  }));
}
